import { Injector, afterNextRender, effect, inject } from '@angular/core';

export type RemovableEntryId = string | number;

export interface RemovalFocus {
  /**
   * Call right before requesting the removal. `index` is the position of the
   * (first) removed entry and is handed to `focus` once the removal happened.
   */
  expect(
    ids: RemovableEntryId | readonly RemovableEntryId[],
    index: number
  ): void;
}

/**
 * Moves the focus to a sensible neighbour after list entries were removed,
 * because the removed row takes the focused control with it.
 *
 * The focus only moves once the expected entries have left the list. A
 * cancelled or failed delete keeps them in the list, so nothing happens. A
 * reload that replaces the whole list (e.g. tenant switch) is ignored as well.
 *
 * Must be called in an injection context.
 */
export function focusAfterRemoval(options: {
  ids: () => readonly RemovableEntryId[];
  focus: (index: number) => void;
}): RemovalFocus {
  const injector = inject(Injector);
  let pending: {
    removed: readonly RemovableEntryId[];
    remaining: ReadonlySet<RemovableEntryId>;
    index: number;
  } | null = null;

  effect(() => {
    const ids = options.ids();
    const current = pending;
    if (!current || current.removed.some((id) => ids.includes(id))) return;

    pending = null;
    const listReplaced =
      current.remaining.size > 0 &&
      !ids.some((id) => current.remaining.has(id));
    if (listReplaced) return;

    afterNextRender(() => options.focus(current.index), { injector });
  });

  return {
    expect(ids, index) {
      const removed: readonly RemovableEntryId[] =
        typeof ids === 'object' ? ids : [ids];
      pending = {
        removed,
        remaining: new Set(options.ids().filter((id) => !removed.includes(id))),
        index,
      };
    },
  };
}
